import { useState } from 'react';
import CardPicker, { CardTile } from './CardPicker';
import { COIN_LABEL } from '../ocgCore';
import { ownedCardsByLocation } from './cardLocations';
import { playerColor, usePlayerColors } from './common';

/**
 * 「登録」の値を選ぶ別ウィンドウ（Issue #339）。選んだ時点で反映する（Issue #332 と同じ）。
 * 選んだものは onAnswer({ code } | { value } | { picks }) でページへ（ページが OcgAnswer の register_key で送り、登録の中身への直し方はサーバー。Issue #669）。
 * - ドロー: そのプレイヤーが持ち主のカードを今ある場所ごとの画像から（CardPicker）→ { code }
 * - コイン: 表 / 裏、サイコロ: 1〜6 → { value }
 * - ランダム選択: 候補から count 枚 → 確定で { picks }（その選択の呼び出しをまとめて差し替えるのはサーバー）
 *
 * Props: item / board / groups（ドローの候補。渡せば board から作らない）/ names / thumbs / playerLabel / busy / onAnswer / onClose。
 * 枠の色はページが配るプレイヤーの色（usePlayerColors）
 */
export default function RegisterPickDialog({ item, board = null, groups: givenGroups = null, names = {}, thumbs = {}, playerLabel = (p) => `P${p + 1}`,
  busy = false, onAnswer, onClose }) {
  const [picked, setPicked] = useState(item?.type === 'select' ? item.chosen : []);
  const colors = usePlayerColors();
  if (!item) return null;
  const color = playerColor(item.player, colors) ?? '#6a7690';
  const tile = (code) => ({ code: String(code), name: names[String(code)] ?? String(code), image: thumbs[String(code)] ?? null });
  const done = (fn) => { onClose?.(); fn(); };
  let body = null;
  if (item.type === 'draw') {
    const groups = givenGroups ?? ownedCardsByLocation(board, item.team, { names, thumbs });
    body = <CardPicker groups={groups} selectedCode={item.code != null ? String(item.code) : null} playerLabel={playerLabel}
                       onPick={(code) => done(() => onAnswer?.({ code: Number(code) }))}
                       onClose={onClose} />;
  } else if (item.type === 'coin' || item.type === 'dice') {
    const options = item.type === 'coin' ? [0, 1] : [1, 2, 3, 4, 5, 6];
    body = (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: 8 }}>
        {options.map((v) => (
          <button key={v} type="button" className={`btn ${item.value === v ? 'btn-primary' : 'btn-secondary'}`} disabled={busy}
                  style={{ minWidth: 64, fontSize: '1.1rem' }}
                  onClick={() => done(() => onAnswer?.({ value: v }))}>
            {item.type === 'coin' ? COIN_LABEL[v] : v}
          </button>
        ))}
      </div>
    );
  } else if (item.type === 'select') {
    const { select } = item;
    const toggle = (i) => setPicked((p) => (p.includes(i) ? p.filter((x) => x !== i) : (p.length < select.count ? [...p, i] : p)));
    body = (
      <div style={{ padding: 8 }}>
        <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 6 }}>{select.count} 枚選ぶ（{picked.length} 枚選択中）</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {select.candidates.map((c, i) => (
            <span key={i} data-candidate={i} style={{ textAlign: 'center' }}>
              <CardTile card={tile(c.code)} selected={picked.includes(i)} onClick={busy ? undefined : () => toggle(i)} />
            </span>
          ))}
        </div>
        <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy || picked.length !== select.count}
                onClick={() => done(() => onAnswer?.({ picks: picked }))}>確定</button>
      </div>
    );
  }
  return (
    <div role="dialog" aria-label="登録の選び直し" data-testid="register-pick-dialog"
         style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 260,
           width: 'min(640px, calc(100vw - 24px))', maxHeight: 'min(80dvh, 640px)', display: 'flex', flexDirection: 'column',
           background: '#1b1f25', border: `2px solid ${color}`, borderRadius: 10, boxShadow: '0 10px 40px rgba(0,0,0,0.7)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #333' }}>
        <strong>{item.player === 0 || item.player === 1 ? `${playerLabel(item.player)} ` : ''}{item.label}</strong>
        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>を選び直す</span>
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>閉じる</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 4 }}>{body}</div>
    </div>
  );
}
